import React, { useEffect, useState } from 'react';
import { apiFetch } from '@/utils/api';
import { Button } from "@/components/ui/button";
import { useI18n } from '@/hooks/useI18n';

interface PolicyType {
  id: string;
  name: string;
}

interface PolicyResponse {
  id: string;
  organization_id: string;
  name: string;
  policy_type_id: string;
  policy_type_name?: string | null;
  units: 'hours' | 'days';
  is_unlimited: boolean;
  balance_limit?: number | null;
  accrual_method: 'fixed_amount' | 'earned_per_hour';
  accrual_cycle?: 'all_at_once' | 'per_pay_period' | null;
  earned_units?: number | null;
  per_hours_worked?: number | null;
  requires_admin_approval: boolean;
  carryover_limit_enabled: boolean;
  carryover_limit_value?: number | null;
  sun_hours?: number | null;
  mon_hours?: number | null;
  tue_hours?: number | null;
  wed_hours?: number | null;
  thu_hours?: number | null;
  fri_hours?: number | null;
  sat_hours?: number | null;
  employee_ids: string[];
  created_at: string;
  updated_at?: string | null;
}

interface PolicyListResponse {
  policies: PolicyResponse[];
  total: number;
  page: number;
  per_page: number;
}

interface EmployeeItem {
  id: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

function usePolicyTypes() {
  const [types, setTypes] = useState<PolicyType[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiFetch('/api/timesheet/time-off-policies/types')
      .then((r) => r.json())
      .then((data) => setTypes(data || []))
      .finally(() => setLoading(false));
  }, []);
  return { types, loading };
}

function useEmployees() {
  const [employees, setEmployees] = useState<EmployeeItem[]>([]);
  useEffect(() => {
    apiFetch('/api/people/employees/?limit=100&is_active=true')
      .then((r) => r.json())
      .then((data) => setEmployees(data?.employees || []))
      .catch(() => setEmployees([]));
  }, []);
  return employees;
}

const emptyForm = {
  name: '',
  policy_type_id: '',
  units: 'hours' as 'hours' | 'days',
  is_unlimited: false,
  balance_limit: '' as string | number | '',
  accrual_method: 'fixed_amount' as 'fixed_amount' | 'earned_per_hour',
  accrual_cycle: 'all_at_once' as 'all_at_once' | 'per_pay_period',
  earned_units: '' as string | number | '',
  per_hours_worked: '' as string | number | '',
  requires_admin_approval: false,
  carryover_limit_enabled: false,
  carryover_limit_value: '' as string | number | '',
  sun_hours: '' as string | number | '',
  mon_hours: '' as string | number | '',
  tue_hours: '' as string | number | '',
  wed_hours: '' as string | number | '',
  thu_hours: '' as string | number | '',
  fri_hours: '' as string | number | '',
  sat_hours: '' as string | number | '',
  employee_ids: [] as string[],
};

export default function TimeOffPoliciesTab() {
  const { t } = useI18n();
  const [list, setList] = useState<PolicyListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 50;

  const { types } = usePolicyTypes();
  const employees = useEmployees();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PolicyResponse | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [error, setError] = useState<string | null>(null);

  const fetchList = () => {
    setLoading(true);
    apiFetch(`/api/timesheet/time-off-policies?page=${page}&per_page=${perPage}`)
      .then((r) => r.json())
      .then((data) => setList(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onCreateClick = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowForm(true);
    setError(null);
  };

  const onEditClick = (p: PolicyResponse) => {
    setEditing(p);
    setForm({
      name: p.name,
      policy_type_id: p.policy_type_id,
      units: p.units,
      is_unlimited: p.is_unlimited,
      balance_limit: p.balance_limit ?? '',
      accrual_method: p.accrual_method,
      accrual_cycle: (p.accrual_cycle ?? 'all_at_once') as any,
      earned_units: p.earned_units ?? '',
      per_hours_worked: p.per_hours_worked ?? '',
      requires_admin_approval: p.requires_admin_approval,
      carryover_limit_enabled: p.carryover_limit_enabled,
      carryover_limit_value: p.carryover_limit_value ?? '',
      sun_hours: p.sun_hours ?? '',
      mon_hours: p.mon_hours ?? '',
      tue_hours: p.tue_hours ?? '',
      wed_hours: p.wed_hours ?? '',
      thu_hours: p.thu_hours ?? '',
      fri_hours: p.fri_hours ?? '',
      sat_hours: p.sat_hours ?? '',
      employee_ids: p.employee_ids || [],
    });
    setShowForm(true);
    setError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ ...emptyForm });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const body: any = {
      name: form.name,
      policy_type_id: form.policy_type_id,
      units: form.units,
      is_unlimited: !!form.is_unlimited,
      balance_limit: form.is_unlimited || form.balance_limit === '' ? null : Number(form.balance_limit),
      accrual_method: form.accrual_method,
      accrual_cycle: form.accrual_method === 'fixed_amount' ? form.accrual_cycle : null,
      earned_units:
        form.accrual_method === 'earned_per_hour' && form.earned_units !== '' ? Number(form.earned_units) : null,
      per_hours_worked:
        form.accrual_method === 'earned_per_hour' && form.per_hours_worked !== ''
          ? Number(form.per_hours_worked)
          : null,
      requires_admin_approval: !!form.requires_admin_approval,
      carryover_limit_enabled: !!form.carryover_limit_enabled,
      carryover_limit_value:
        form.carryover_limit_enabled && form.carryover_limit_value !== ''
          ? Number(form.carryover_limit_value)
          : null,
      sun_hours: form.sun_hours === '' ? null : Number(form.sun_hours),
      mon_hours: form.mon_hours === '' ? null : Number(form.mon_hours),
      tue_hours: form.tue_hours === '' ? null : Number(form.tue_hours),
      wed_hours: form.wed_hours === '' ? null : Number(form.wed_hours),
      thu_hours: form.thu_hours === '' ? null : Number(form.thu_hours),
      fri_hours: form.fri_hours === '' ? null : Number(form.fri_hours),
      sat_hours: form.sat_hours === '' ? null : Number(form.sat_hours),
      employee_ids: form.employee_ids,
    };

    const method = editing ? 'PUT' : 'POST';
    const path = editing ? `/api/timesheet/time-off-policies/${editing.id}` : '/api/timesheet/time-off-policies';

    const resp = await apiFetch(path, {
      method,
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const msg = await resp.text();
      setError(msg || t('peopleSettings.timeOffPolicies.requestFailed', 'admin'));
      return;
    }

    await resp.json();
    closeForm();
    fetchList();
  };

  const onDelete = async (p: PolicyResponse) => {
    if (!confirm(t('peopleSettings.timeOffPolicies.deleteConfirm', 'admin', { name: p.name }))) return;
    const resp = await apiFetch(`/api/timesheet/time-off-policies/${p.id}`, { method: 'DELETE' });
    if (resp.ok) fetchList();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('peopleSettings.timeOffPolicies.title', 'admin')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('peopleSettings.timeOffPolicies.subtitle', 'admin')}
          </p>
        </div>
        <Button onClick={onCreateClick}>
          {t('peopleSettings.timeOffPolicies.newPolicy', 'admin')}
        </Button>
      </div>

      {loading ? (
        <div>{t('actions.loading', 'common')}</div>
      ) : (
        <div className="border rounded overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">{t('peopleSettings.timeOffPolicies.columns.name', 'admin')}</th>
                <th className="px-3 py-2 text-left">{t('peopleSettings.timeOffPolicies.columns.type', 'admin')}</th>
                <th className="px-3 py-2 text-left">{t('peopleSettings.timeOffPolicies.columns.units', 'admin')}</th>
                <th className="px-3 py-2 text-left">{t('peopleSettings.timeOffPolicies.columns.accrual', 'admin')}</th>
                <th className="px-3 py-2 text-left">{t('peopleSettings.timeOffPolicies.columns.approval', 'admin')}</th>
                <th className="px-3 py-2 text-left">{t('peopleSettings.timeOffPolicies.columns.carryover', 'admin')}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list?.policies?.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="px-3 py-2">{p.policy_type_name || p.policy_type_id}</td>
                  <td className="px-3 py-2">
                    {p.units === 'hours' ? t('peopleSettings.timeOffPolicies.units.hours', 'admin') : t('peopleSettings.timeOffPolicies.units.days', 'admin')}
                  </td>
                  <td className="px-3 py-2">
                    {p.is_unlimited
                      ? t('peopleSettings.timeOffPolicies.unlimited', 'admin')
                      : p.accrual_method === 'fixed_amount'
                        ? t('peopleSettings.timeOffPolicies.fixedAccrual', 'admin', {
                            amount: p.balance_limit ?? 0,
                            cycle: t(`peopleSettings.timeOffPolicies.accrualCycles.${p.accrual_cycle || 'all_at_once'}`, 'admin'),
                          })
                        : t('peopleSettings.timeOffPolicies.earnedAccrual', 'admin', {
                            earned: p.earned_units ?? 0,
                            per: p.per_hours_worked ?? 0,
                          })}
                  </td>
                  <td className="px-3 py-2">{p.requires_admin_approval ? t('actions.yes', 'common') : t('actions.no', 'common')}</td>
                  <td className="px-3 py-2">
                    {p.carryover_limit_enabled ? `${p.carryover_limit_value ?? 0}` : t('actions.no', 'common')}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      onClick={() => onEditClick(p)}
                      className="px-2 py-1 text-blue-700 hover:underline"
                    >
                      {t('actions.edit', 'common')}
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      className="px-2 py-1 text-red-700 hover:underline"
                    >
                      {t('actions.delete', 'common')}
                    </button>
                  </td>
                </tr>
              ))}
              {(!list || list.policies.length === 0) && (
                <tr>
                  <td className="px-3 py-4 text-gray-500" colSpan={7}>{t('peopleSettings.timeOffPolicies.empty', 'admin')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {list && list.total > list.per_page && (
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            {t('actions.previous', 'common')}
          </button>
          <div>{t('peopleSettings.timeOffPolicies.page', 'admin', { page })}</div>
          <button
            disabled={page * perPage >= list.total}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            {t('actions.next', 'common')}
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white w-full max-w-4xl rounded shadow-lg p-6 space-y-4 my-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editing ? t('peopleSettings.timeOffPolicies.editTitle', 'admin') : t('peopleSettings.timeOffPolicies.newTitle', 'admin')}
              </h2>
              <button onClick={closeForm} className="text-gray-600 hover:text-black">✕</button>
            </div>

            {error && <div className="text-red-600 text-sm">{error}</div>}

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.name', 'admin')}</label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.policyType', 'admin')}</label>
                  <select
                    className="w-full border rounded px-2 py-1"
                    value={form.policy_type_id}
                    onChange={(e) => setForm({ ...form, policy_type_id: e.target.value })}
                    required
                  >
                    <option value="">{t('actions.select', 'common')}...</option>
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.units', 'admin')}</label>
                  <select
                    className="w-full border rounded px-2 py-1"
                    value={form.units}
                    onChange={(e) => setForm({ ...form, units: e.target.value as any })}
                  >
                    <option value="hours">{t('peopleSettings.timeOffPolicies.units.hours', 'admin')}</option>
                    <option value="days">{t('peopleSettings.timeOffPolicies.units.days', 'admin')}</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={form.is_unlimited}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        is_unlimited: checked,
                        carryover_limit_enabled: checked ? false : prev.carryover_limit_enabled,
                        carryover_limit_value: checked ? '' : prev.carryover_limit_value,
                        earned_units: checked ? '' : prev.earned_units,
                        per_hours_worked: checked ? '' : prev.per_hours_worked,
                      }));
                    }}
                  />
                  <span>{t('peopleSettings.timeOffPolicies.form.unlimitedBalance', 'admin')}</span>
                </div>
                {!form.is_unlimited && (
                  <div>
                    <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.balanceLimit', 'admin')}</label>
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1"
                      value={form.balance_limit}
                      onChange={(e) => setForm({ ...form, balance_limit: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {!form.is_unlimited && (
                <div className="border rounded p-3">
                  <div className="font-medium mb-2">{t('peopleSettings.timeOffPolicies.form.accrual', 'admin')}</div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={form.accrual_method === 'fixed_amount'}
                        onChange={() => setForm({ ...form, accrual_method: 'fixed_amount' })}
                      />
                      {t('peopleSettings.timeOffPolicies.form.fixedAmount', 'admin')}
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={form.accrual_method === 'earned_per_hour'}
                        onChange={() => setForm({ ...form, accrual_method: 'earned_per_hour' })}
                      />
                      {t('peopleSettings.timeOffPolicies.form.earnedPerHour', 'admin')}
                    </label>
                  </div>

                  {form.accrual_method === 'fixed_amount' ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.accrualCycle', 'admin')}</label>
                        <select
                          className="w-full border rounded px-2 py-1"
                          value={form.accrual_cycle}
                          onChange={(e) => setForm({ ...form, accrual_cycle: e.target.value as any })}
                        >
                          <option value="all_at_once">{t('peopleSettings.timeOffPolicies.accrualCycles.all_at_once', 'admin')}</option>
                          <option value="per_pay_period">{t('peopleSettings.timeOffPolicies.accrualCycles.per_pay_period', 'admin')}</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.earnUnits', 'admin')}</label>
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1"
                          value={form.earned_units}
                          onChange={(e) => setForm({ ...form, earned_units: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.perHoursWorked', 'admin')}</label>
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1"
                          value={form.per_hours_worked}
                          onChange={(e) => setForm({ ...form, per_hours_worked: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.requires_admin_approval}
                    onChange={(e) => setForm({ ...form, requires_admin_approval: e.target.checked })}
                  />
                  <span>{t('peopleSettings.timeOffPolicies.form.requiresApproval', 'admin')}</span>
                </div>
                {!form.is_unlimited && (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.carryover_limit_enabled}
                        onChange={(e) => setForm({ ...form, carryover_limit_enabled: e.target.checked })}
                      />
                      <span>{t('peopleSettings.timeOffPolicies.form.carryoverLimit', 'admin')}</span>
                    </div>
                    {form.carryover_limit_enabled && (
                      <div>
                        <label className="block text-sm mb-1">{t('peopleSettings.timeOffPolicies.form.carryoverLimitValue', 'admin')}</label>
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1"
                          value={form.carryover_limit_value}
                          onChange={(e) => setForm({ ...form, carryover_limit_value: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <div className="font-medium mb-2">{t('peopleSettings.timeOffPolicies.form.workdays', 'admin')}</div>
                <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                  {[
                    { key: 'sun_hours', label: t('peopleSettings.timeOffPolicies.days.sun', 'admin') },
                    { key: 'mon_hours', label: t('peopleSettings.timeOffPolicies.days.mon', 'admin') },
                    { key: 'tue_hours', label: t('peopleSettings.timeOffPolicies.days.tue', 'admin') },
                    { key: 'wed_hours', label: t('peopleSettings.timeOffPolicies.days.wed', 'admin') },
                    { key: 'thu_hours', label: t('peopleSettings.timeOffPolicies.days.thu', 'admin') },
                    { key: 'fri_hours', label: t('peopleSettings.timeOffPolicies.days.fri', 'admin') },
                    { key: 'sat_hours', label: t('peopleSettings.timeOffPolicies.days.sat', 'admin') }
                  ].map((d) => (
                    <div key={d.key}>
                      <label className="block text-xs mb-1">{d.label}</label>
                      <input
                        type="number"
                        className="w-full border rounded px-2 py-1"
                        value={(form as any)[d.key]}
                        onChange={(e) => setForm({ ...form, [d.key]: e.target.value }) as any}
                        placeholder={t('peopleSettings.timeOffPolicies.form.hoursPlaceholder', 'admin')}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-medium mb-2">{t('peopleSettings.timeOffPolicies.form.appliesTo', 'admin')}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-48 overflow-auto border rounded p-2">
                  {employees.map((emp) => (
                    <label key={emp.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.employee_ids.includes(emp.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            employee_ids: checked
                              ? [...prev.employee_ids, emp.id]
                              : prev.employee_ids.filter((id) => id !== emp.id),
                          }));
                        }}
                      />
                      <span>
                        {emp.first_name} {emp.last_name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeForm} className="px-3 py-2 border rounded">
                  {t('actions.cancel', 'common')}
                </button>
                <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded">
                  {editing ? t('actions.save', 'common') : t('actions.create', 'common')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
